import { useMemo, useRef } from "react";
import { currencyToReadable } from "utils/DashboardUtils";
import useAllTags from "./useAllTags";
import useFieldMap from "./useFieldMap";
import dayjs from "dayjs";
import FieldUtils from "../utils/FieldUtils";

const handleMethodDisplay = (filter) => {
  let displayMethod;

  switch (filter?.Method) {
    case "not-equals":
      displayMethod = "does not equal";
      break;
    case "not-between":
      displayMethod = "is not between";
      break;
    case "is-not-empty":
      displayMethod = "is not empty";
      break;
    case "is-empty":
      displayMethod = "is empty";
      break;
    case "lt":
      displayMethod = "is less than";
      break;
    case "lte":
      displayMethod = "is less than or equal to";
      break;
    case "gt":
      displayMethod = "is greater than";
      break;
    case "gte":
      displayMethod = "is greater than or equal to";
      break;
    default:
      displayMethod = filter?.Method;
  }

  return displayMethod;
};

const useReadableText = ({ query, fallbackMessage, moduleAPIName }) => {
  const fieldTypeMap = useFieldMap(moduleAPIName, "FieldType");
  const fieldNameMap = useFieldMap(moduleAPIName, "Name");
  const { getTagNames } = useAllTags(moduleAPIName);
  const handleValueDisplayRef = useRef();

  handleValueDisplayRef.current = handleValueDisplay;

  const getReadableText = (providedQuery) => {
    return getTheReadableText({
      query: providedQuery,
      fallbackMessage,
      handleValueDisplay: handleValueDisplayRef.current,
      fieldNameMap,
      fieldTypeMap,
      getTagNames,
    });
  };

  const readableText = useMemo(() => {
    if (!fieldTypeMap || !fieldNameMap) return fallbackMessage;
    if (!Object.keys(fieldTypeMap).length || !Object.keys(fieldNameMap).length)
      return fallbackMessage;

    return getTheReadableText({
      query: query,
      fallbackMessage,
      handleValueDisplay: handleValueDisplayRef.current,
      fieldNameMap,
      fieldTypeMap,
      getTagNames,
    });
  }, [fallbackMessage, query, fieldNameMap, fieldTypeMap, getTagNames]);

  return { readableText, getReadableText };
};

export function getTheReadableText({
  query,
  fallbackMessage,
  handleValueDisplay,
  fieldNameMap,
  fieldTypeMap,
  getTagNames,
}) {
  if (!query || !query?.Filters || query?.Filters?.length < 1) {
    return fallbackMessage || null;
  }

  let text = "";

  query?.Filters.forEach((q, index) => {
    const isLast = index === query.Filters.length - 1;

    if (q.Group) {
      q.Filters?.forEach((filter, index) => {
        const isLast = index === q.Filters.length - 1;
        const fieldName = fieldNameMap[filter.Field];

        const readableGroupText = [
          fieldName || filter.Field,
          handleMethodDisplay(filter),
          handleValueDisplay({ filter, fieldTypeMap, getTagNames }),
          !isLast ? "AND " : "",
        ].join(" ");

        text += readableGroupText;
      });
    } else {
      const fieldName = fieldNameMap[q.Field];

      const readableText = [
        fieldName || q.Field,
        handleMethodDisplay(q),
        handleValueDisplay({ filter: q, fieldTypeMap, getTagNames }),
        !isLast ? "AND " : "",
      ].join(" ");

      text += readableText;
    }

    if (!isLast) text += " OR ";
  });

  return text;
}

export function handleValueDisplay({ filter, fieldTypeMap, getTagNames }) {
  if (!filter?.Value) return "";

  let displayValue = filter.Value;
  const type = fieldTypeMap[filter?.Field];

  if (typeof filter.Value === "string" && filter.Value.startsWith("{{")) {
    return filter.Value;
  }

  if (filter?.Value) {
    displayValue = getDisplayValue(type, filter.Value, getTagNames);
  }

  if (filter?.Min) {
    let min = getDisplayValue(type, filter.Min, getTagNames);
    let max = getDisplayValue(type, filter.Max, getTagNames);

    displayValue = `${min} and ${max}`;
  }

  if (typeof displayValue === "object" && displayValue?.length) {
    displayValue = displayValue.join(", ");
  } else if (typeof displayValue === "object") {
    if (displayValue._RecordName) {
      displayValue = displayValue._RecordName;
    }
    displayValue = JSON.stringify(displayValue);
  }

  return displayValue;
}

export function getDisplayValue(type, value, getTagNames) {
  const isCurrency = type === "Currency" || type === "CurrencyInterval";

  if (type === "SingleReference") {
    return value._RecordName;
  }
  if (type === "MultiReference" && Array.isArray(value)) {
    return value.map((v) => v._RecordName).join(", ");
  }
  if (type === "RichText") {
    return value?.PlainText;
  }
  if (["Email", "Phone", "URL"].includes(type)) {
    return FieldUtils.getPrimaryOrFirstValue(value, type);
  }
  if (isCurrency) {
    return currencyToReadable(value);
  }
  if (type === "Tags") {
    return getTagNames(value);
  }
  if (type === "DateTime") {
    return dayjs(value).format("LLL"); // June 14th, 2023 8:34 AM - Locale Support
  }
  if (type === "Date") {
    return dayjs(value).format("LL"); // June 14th, 2023 - Locale Support
  }
  if (type === "Time") {
    return dayjs(value).format("LT"); // 8:51 AM - Locale Support
  }

  if (value && typeof value !== "string") {
    try {
      const stringified = JSON.stringify(value);
      return stringified;
    } catch {
      return value;
    }
  }

  return value;
}

export default useReadableText;
